import React, { useRef, useEffect } from "react";
import {
  Center,
  NumberInput as MantineNumberInput,
  NumberInputProps,
  Tooltip,
  Flex,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { InfoCircle } from "iconsax-react";

export interface InputNumberProps extends NumberInputProps {
  label: React.ReactNode;
  hasIcon?: boolean;
  showControls?: boolean; // Nova prop para controlar a exibição dos botões
}

/**
 * InputNumber
 *
 * Componente de número com estilização padrão do produto,
 * recebendo todas as props de `Mantine NumberInput` + `label`,
 * com o label exibido DENTRO do input (lado esquerdo).
 */
const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  (props, ref) => {
    const {
      styles,
      label,
      hasIcon,
      showControls = false,
      placeholder,
      ...rest
    } = props;
    const theme = useMantineTheme();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    // Encontra o input interno do NumberInput após renderizar
    useEffect(() => {
      const findInput = () => {
        if (wrapperRef.current) {
          const input = wrapperRef.current.querySelector(
            'input[type="text"]'
          ) as HTMLInputElement;
          if (input) {
            inputRef.current = input;
          }
        }
      };

      // Tenta encontrar imediatamente
      findInput();

      // Se não encontrar, tenta novamente após um pequeno delay (para garantir que o DOM foi renderizado)
      const timeoutId = setTimeout(findInput, 10);

      return () => clearTimeout(timeoutId);
    });

    const defaultStyles: NumberInputProps["styles"] = (theme) => ({
      input: {
        textAlign: "right",
        fontSize: "24px !important",
        fontWeight: 600,
        color: theme.colors.basic[4],
        maxHeight: "42px !important",
        height: "42px !important",
        minHeight: "42px !important",
        padding: hasIcon ? "0 50px 0 0 !important" : "0 30px 0 0!important",
        background: "transparent",
        border: "none !important",
        outline: "none !important",
        fontFamily: "Poppins",
        "::placeholder": {
          fontSize: hasIcon ? "14px !important" : "24px !important",
          color: hasIcon
            ? `${theme.colors.basic[3]} !important`
            : `${theme.colors.basic[4]} !important`,
          fontWeight: 500,
          opacity: 1,
        },
        "::-webkit-input-placeholder": {
          fontSize: hasIcon ? "14px !important" : "24px !important",
          color: hasIcon
            ? `${theme.colors.basic[3]} !important`
            : `${theme.colors.basic[4]} !important`,
          fontWeight: 500,
          bottom: "20px",
          opacity: 1,
        },
        "::-moz-placeholder": {
          fontSize: hasIcon ? "14px !important" : "24px !important",
          color: hasIcon
            ? `${theme.colors.basic[3]} !important`
            : `${theme.colors.basic[4]} !important`,
          fontWeight: 500,
          opacity: 1,
          bottom: "20px",
        },
        ":-ms-input-placeholder": {
          fontSize: hasIcon ? "14px !important" : "24px !important",
          color: hasIcon
            ? `${theme.colors.basic[3]} !important`
            : `${theme.colors.basic[4]} !important`,
          fontWeight: 500,
          opacity: 1,
          bottom: "20px",
        },
      },

      wrapper: {
        marginTop: "0px !important",
        border: "none !important",
        outline: "none !important",
        maxHeight: "42px !important",
        height: "42px !important",
        minHeight: "42px !important",
      },

      controls: {
        opacity: 1,
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        borderLeft: "1px solid",
      },

      rightSection: {
        height: "54px",
        top: "-6px",
        justifyContent: "start",
        margin: "0 !important",
        width: "25px",
        right: "0",
      },

      control: {
        border: "none !important",
        borderRadius: "4px",
        color: theme.colors.basic[5],
        backgroundColor: "white",
        width: "24px",
        height: "27px",
        minHeight: "27px",
        padding: "0 !important",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        "&:hover": {
          backgroundColor: theme.colors.basic[2],
        },
        "&:active": {
          transform: "scale(0.95)",
        },
        "& svg": {
          width: "16px",
          height: "16px",
        },
      },
    });

    // Função para focar o input ao clicar no Flex
    const handleFlexClick = () => {
      inputRef.current?.focus();
    };

    return (
      <Flex
        justify="space-between"
        align="center"
        w="100%"
        p="6px 0 6px 16px"
        gap="8px"
        onClick={handleFlexClick}
        style={{
          border: `1px solid ${theme.colors.basic[2]}`,
          borderRadius: "0.5rem",
          cursor: "text",
          position: "relative",
        }}
      >
        <Text
          style={{
            color: theme.colors.basic[4],
            fontSize: "14px",
            fontWeight: 400,
            whiteSpace: "nowrap",
            fontFamily: "Poppins",
          }}
        >
          {label}
        </Text>

        <div ref={wrapperRef} style={{ flex: 1 }}>
          <MantineNumberInput
            ref={ref}
            styles={styles ?? defaultStyles}
            hideControls={!showControls} // Controla a exibição dos botões
            placeholder={hasIcon ? "" : placeholder}
            {...rest}
          />
        </div>
        <Flex
          align={"center"}
          style={{ position: "absolute", right: "24px" }}
          gap={"4px"}
          justify={"center"}
        >
          {hasIcon && (
            <>
              {!!rest.value ? null : (
                <Text
                  c={theme.colors.basic[3]}
                  fz={"14px"}
                  ff={"Poppins"}
                  ta={"center"}
                >
                  {placeholder}
                </Text>
              )}
              <Tooltip label="Valores de 1 a 100" withArrow position="top-end">
                <Flex align={"end"}>
                  <InfoCircle
                    size={20}
                    color={theme.colors.basic[2]}
                    variant="Bold"
                  />
                </Flex>
              </Tooltip>
            </>
          )}
        </Flex>
      </Flex>
    );
  }
);

InputNumber.displayName = "InputNumber";

export default InputNumber;
